/**
 * project-group-by-utils.ts
 *
 * Grouping logic and subtotal calculations for the Projects list view.
 *
 * Supported group-by fields (categorical / low-cardinality only):
 *   - project-status   (Project.statusId)
 *   - project-type     (Project.type)
 *   - health-indicator (Project.health)
 *   - project-manager  (Project.projectManagerId → Employee.name)
 *   - client-name      (Project.clientIds → Client.name)
 *   - industry         (Project.clientIds → Client.industry)
 *
 * Subtotals (sum-able numeric fields):
 *   - budget           (budgetRevenue)
 *   - actual-sales     (actualRevenue)
 *   - cost-budget      (budgetLabour + budgetExpense)
 *   - actual-cost      (actualLabour + actualExpense)
 *   - gross-profit     (actualRevenue - actualLabour - actualExpense)
 *   - wip-amount       (wipAmount)
 *   - allocated-hours  (budgetHours)
 *   - actual-hours     (actualHours)
 *   - project-count    always shown as the number of projects in the group
 */

import type { Project, Client, Employee } from '@/types';

// ─── Group-by option definition ───────────────────────────────────────

export interface GroupByOption {
  id: GroupById;
  label: string;
}

export type GroupById =
  | 'project-status'
  | 'project-type'
  | 'health-indicator'
  | 'project-manager'
  | 'client-name';

export const GROUP_BY_OPTIONS: GroupByOption[] = [
  { id: 'project-status',   label: 'Project Status' },
  { id: 'project-manager',  label: 'Project Manager' },
  { id: 'client-name',      label: 'Client Name' },
  { id: 'health-indicator', label: 'Health' },
  { id: 'project-type',     label: 'Project Type' },
];

// ─── Subtotal definition ──────────────────────────────────────────────

export interface GroupSubtotals {
  count: number;
  budgetRevenue: number;
  actualRevenue: number;
  costBudget: number;
  actualCost: number;
  grossProfit: number;
  wipAmount: number;
  budgetHours: number;
  actualHours: number;
}

// ─── Group structure ──────────────────────────────────────────────────

export interface ProjectGroup {
  /** Stable key used for collapse state (includes parent key path for nested groups) */
  key: string;
  /** Human-readable label shown in the group header */
  label: string;
  /** The GroupById field this group was created from */
  groupById: GroupById;
  /** Depth level — 0 = top level, 1 = first sub-group, etc. */
  depth: number;
  projects: Project[];
  subtotals: GroupSubtotals;
  /** Nested sub-groups when more than one Group By field is active */
  subGroups?: ProjectGroup[];
  /**
   * Raw group values of this group and all its ancestors (server-aggregated groups
   * only). A leaf group turns this path into the filters that load its own rows.
   */
  groupPath?: GroupPathSegment[];
  /** True when this group's rows are paginated by the server rather than held in `projects` */
  serverPaginated?: boolean;
}

/** One level of a group's raw key path, e.g. `{ groupById: 'project-status', key: '2' }`. */
export interface GroupPathSegment {
  groupById: GroupById;
  key: string;
}

// ─── Server-aggregated group tree ─────────────────────────────────────

/** A node of the group tree returned by GET /api/projects/grouped-tree. */
export interface ServerProjectGroupNode {
  /** Raw group value — status ID, type slug, health slug, manager ID, client ID, or `__none__` */
  key: string;
  label: string;
  groupById: string;
  depth: number;
  count: number;
  subtotals: {
    count: number;
    budgetRevenue: number;
    actualRevenue: number;
    costBudget: number;
    actualCost: number;
    /** Derived server-side as actualRevenue − actualCost; never summed from a stored field */
    grossProfit: number;
    wipAmount: number;
    budgetHours: number;
    actualHours: number;
  };
  children: ServerProjectGroupNode[];
}

/** Filters that narrow the project list to the rows of a single leaf group. */
export interface GroupLeafFilters {
  statusId?: number;
  health?: string;
  type?: string;
  managerId?: number;
  clientId?: number;
  /** Group-by fields whose value must be NULL (the "Unassigned" bucket) */
  unassigned: GroupById[];
}

// ─── Reference data (lightweight subset needed for lookups) ──────────

export interface GroupByRefData {
  clients: Client[];
  employees: Employee[];
  /** Dynamic status labels: statusId → label */
  statusLabels: Record<number, string>;
}

// ─── Key extraction helpers ───────────────────────────────────────────

function getGroupKey(
  project: Project,
  groupBy: GroupById,
  ref: GroupByRefData,
): string {
  switch (groupBy) {
    case 'project-status':
      return project.statusId != null ? String(project.statusId) : '__none__';

    case 'project-type':
      return project.type ?? '__none__';

    case 'health-indicator':
      return project.health ?? '__none__';

    case 'project-manager': {
      const emp = ref.employees.find((e) => e.id === project.projectManagerId);
      return emp?.name ?? '__none__';
    }

    case 'client-name': {
      if (!project.clientIds) return '__none__';
      // For multi-client projects use the first client name as the key
      const firstId = project.clientIds.split(',').filter(Boolean)[0];
      const client = ref.clients.find((c) => c.id === parseInt(firstId, 10));
      return client?.name ?? '__none__';
    }

    default:
      return '__none__';
  }
}

export function getGroupLabel(
  key: string,
  groupBy: GroupById,
  ref: GroupByRefData,
): string {
  if (key === '__none__') return 'Unassigned';

  switch (groupBy) {
    case 'project-status': {
      const statusId = parseInt(key, 10);
      return ref.statusLabels[statusId] ?? `Status ${key}`;
    }

    case 'project-type':
      switch (key) {
        case 'fixed-price':        return 'Fixed Price';
        case 'time-and-materials': return 'Time & Materials';
        case 'retainer':           return 'Retainer';
        case 'milestone-based':    return 'Milestone Based';
        default:                   return key;
      }

    case 'health-indicator':
      switch (key) {
        case 'green': return 'Healthy';
        case 'amber': return 'At Risk';
        case 'red':   return 'Critical';
        default:      return key;
      }

    // For manager / client the key IS the display label
    case 'project-manager':
    case 'client-name':
      return key;

    default:
      return key;
  }
}

// ─── Subtotal calculation ─────────────────────────────────────────────

function calcSubtotals(projects: Project[]): GroupSubtotals {
  return projects.reduce<GroupSubtotals>(
    (acc, p) => {
      acc.count          += 1;
      acc.budgetRevenue  += p.budgetRevenue   ?? 0;
      // actual-sales column uses actualRevenue (invoiced revenue ledger)
      acc.actualRevenue  += p.actualRevenue   ?? 0;
      acc.costBudget     += (p.budgetLabour   ?? 0) + (p.budgetExpense  ?? 0);
      // actual-cost column uses actualLabour + actualExpense (resource cost ledger)
      acc.actualCost     += (p.actualLabour   ?? 0) + (p.actualExpense  ?? 0);
      // gross-profit column uses actualSales - actualCosts (transactions-based — matches grid column)
      acc.grossProfit    += (p.actualSales    ?? 0) - (p.actualCosts    ?? 0);
      acc.wipAmount      += p.wipAmount       ?? 0;
      acc.budgetHours    += p.budgetHours     ?? 0;
      acc.actualHours    += p.actualHours     ?? 0;
      return acc;
    },
    {
      count: 0,
      budgetRevenue: 0,
      actualRevenue: 0,
      costBudget: 0,
      actualCost: 0,
      grossProfit: 0,
      wipAmount: 0,
      budgetHours: 0,
      actualHours: 0,
    },
  );
}

// ─── Sort order for group headers ─────────────────────────────────────

const HEALTH_ORDER: Record<string, number> = { red: 0, amber: 1, green: 2, __none__: 3 };

function groupSortKey(group: ProjectGroup, groupBy: GroupById): string | number {
  switch (groupBy) {
    case 'health-indicator':
      return HEALTH_ORDER[group.key] ?? 99;
    default:
      // Alphabetical, but push "Unassigned" to the end
      return group.key === '__none__' ? 'zzz' : group.label.toLowerCase();
  }
}

function sortGroups(groups: ProjectGroup[], groupBy: GroupById): void {
  groups.sort((a, b) => {
    // Use the raw key (last segment) for sort comparisons
    const rawA = a.key.split('::').pop()!;
    const rawB = b.key.split('::').pop()!;
    const tempA: ProjectGroup = { ...a, key: rawA };
    const tempB: ProjectGroup = { ...b, key: rawB };
    const ak = groupSortKey(tempA, groupBy);
    const bk = groupSortKey(tempB, groupBy);
    if (typeof ak === 'number' && typeof bk === 'number') return ak - bk;
    return String(ak).localeCompare(String(bk));
  });
}

// ─── Server tree → ProjectGroup mapping ──────────────────────────────

/**
 * Maps the server-aggregated group tree onto the ProjectGroup shape rendered by
 * ProjectsGroupedView. No project rows are included: every leaf is flagged
 * `serverPaginated` and loads its own page of rows from the project list endpoint
 * using `groupPath`.
 */
export function mapServerGroupTree(
  nodes: ServerProjectGroupNode[],
  groupBy: GroupById[],
  ref: GroupByRefData,
  depth = 0,
  keyPrefix = '',
  parentPath: GroupPathSegment[] = [],
): ProjectGroup[] {
  const currentGroupBy = groupBy[depth];
  if (!currentGroupBy) return [];

  const groups = nodes.map((node) => {
    const compositeKey = keyPrefix ? `${keyPrefix}::${node.key}` : node.key;
    const groupPath = [...parentPath, { groupById: currentGroupBy, key: node.key }];

    const subGroups = node.children.length > 0
      ? mapServerGroupTree(node.children, groupBy, ref, depth + 1, compositeKey, groupPath)
      : undefined;

    // Manager/client labels are names only the server knows; status/type/health keys
    // are resolved locally so dynamic status labels stay in sync with the grid.
    const localLabel = getGroupLabel(node.key, currentGroupBy, ref);
    const usesServerLabel =
      node.key !== '__none__' &&
      (currentGroupBy === 'project-manager' || currentGroupBy === 'client-name');

    return {
      key: compositeKey,
      label: usesServerLabel ? (node.label || localLabel) : localLabel,
      groupById: currentGroupBy,
      depth,
      projects: [],
      subtotals: {
        count: node.subtotals.count,
        budgetRevenue: node.subtotals.budgetRevenue,
        actualRevenue: node.subtotals.actualRevenue,
        costBudget: node.subtotals.costBudget,
        actualCost: node.subtotals.actualCost,
        // Derived server-side, never summed. Reconciles with calcSubtotals'
        // actualSales − actualCosts because usp_Project_GetPaginated already collapses
        // both ledgers into the row it returns: [ActualRevenue] is
        // COALESCE(SalesActual, SalesBudget.TotalActual, ActualRevenue) — the same value
        // the entity's ActualSales getter yields — and [ActualLabour] + [ActualExpense]
        // is COALESCE(CostActual, CostBudget.TotalActual, ActualLabour + ActualExpense),
        // which is ActualCosts.
        grossProfit: node.subtotals.grossProfit,
        wipAmount: node.subtotals.wipAmount,
        budgetHours: node.subtotals.budgetHours,
        actualHours: node.subtotals.actualHours,
      },
      subGroups,
      groupPath,
      serverPaginated: subGroups === undefined,
    } satisfies ProjectGroup;
  });

  sortGroups(groups, currentGroupBy);
  return groups;
}

/**
 * Translates a group's raw key path into the filter params that select exactly the
 * projects belonging to that group.
 */
export function groupPathToFilterParams(path: GroupPathSegment[]): GroupLeafFilters {
  const filters: GroupLeafFilters = { unassigned: [] };

  for (const { groupById, key } of path) {
    if (key === '__none__') {
      filters.unassigned.push(groupById);
      continue;
    }
    switch (groupById) {
      case 'project-status':
        filters.statusId = parseInt(key, 10);
        break;
      case 'health-indicator':
        filters.health = key;
        break;
      case 'project-type':
        filters.type = key;
        break;
      case 'project-manager':
        filters.managerId = parseInt(key, 10);
        break;
      case 'client-name':
        filters.clientId = parseInt(key, 10);
        break;
    }
  }

  return filters;
}

// ─── Main grouping function ───────────────────────────────────────────

/**
 * Recursively buckets projects into nested groups.
 *
 * @param projects  - the set of projects to group
 * @param groupBy   - ordered list of GroupById fields (up to 3). The first
 *                    field is the primary grouping; subsequent fields become
 *                    nested sub-groups within each primary group.
 * @param ref       - reference data for key → label lookups
 * @param depth     - current nesting depth (0 = top level)
 * @param keyPrefix - parent key path used to keep collapse-state keys unique
 *                    across nested groups (e.g. "green::alice-johnson")
 */
export function groupProjects(
  projects: Project[],
  groupBy: GroupById[],
  ref: GroupByRefData,
  depth = 0,
  keyPrefix = '',
): ProjectGroup[] {
  if (!groupBy.length) return [];

  const currentGroupBy = groupBy[0];
  const remainingGroupBy = groupBy.slice(1);

  // Bucket projects by group key
  const map = new Map<string, Project[]>();
  for (const project of projects) {
    const key = getGroupKey(project, currentGroupBy, ref);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(project);
  }

  // Build group objects
  const groups: ProjectGroup[] = [];
  for (const [rawKey, grpProjects] of map.entries()) {
    // Prefix the key so sibling groups at different levels never share a collapse key
    const compositeKey = keyPrefix ? `${keyPrefix}::${rawKey}` : rawKey;

    // Recursively build sub-groups if more Group By fields remain
    const subGroups =
      remainingGroupBy.length > 0
        ? groupProjects(grpProjects, remainingGroupBy, ref, depth + 1, compositeKey)
        : undefined;

    groups.push({
      key: compositeKey,
      label: getGroupLabel(rawKey, currentGroupBy, ref),
      groupById: currentGroupBy,
      depth,
      projects: grpProjects,
      subtotals: calcSubtotals(grpProjects),
      subGroups,
    });
  }

  sortGroups(groups, currentGroupBy);

  return groups;
}
